import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Shield, Clock, CheckSquare, BarChart3 } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_emp-management-9/artifacts/wiqwkhla_Untitled%20design%20%286%29.jpg";

const features = [
  { icon: Shield, label: "Secure RBAC" },
  { icon: Clock, label: "Time Tracking" },
  { icon: CheckSquare, label: "Task Management" },
  { icon: BarChart3, label: "Reports & Analytics" },
];

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name || "User"}!`);
      navigate(user.role === "admin" ? "/admin/dashboard" : "/employee/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1a2332] flex-col items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#38b6e8]/5 rounded-full" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#38b6e8]/5 rounded-full" />
        <div className="relative z-10 text-center max-w-sm">
          <img src={LOGO} alt="AppDroid" className="h-20 w-auto object-contain mx-auto mb-8" />
          <h1 className="text-2xl font-bold text-white mb-3">Employee Management System</h1>
          <p className="text-slate-400 mb-10 leading-relaxed">
            Manage your workforce with efficiency, transparency and intelligence.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {features.map(({ icon: Icon, label }) => (
              <div key={label} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 flex flex-col items-center gap-2">
                <div className="p-2 bg-[#38b6e8]/10 rounded-lg">
                  <Icon size={18} className="text-[#38b6e8]" />
                </div>
                <p className="text-slate-300 text-xs font-medium">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <img src={LOGO} alt="AppDroid" className="h-14 w-auto object-contain mx-auto" />
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <div className="mb-7">
              <h2 className="text-2xl font-bold text-slate-900 mb-1.5">Welcome back</h2>
              <p className="text-slate-500 text-sm">Sign in to your account to continue</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium text-sm">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@appdroidtech.com"
                  className="mt-1.5 focus-visible:ring-[#38b6e8] border-slate-200"
                  data-testid="login-email-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700 font-medium text-sm">Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pr-10 focus-visible:ring-[#38b6e8] border-slate-200"
                    data-testid="login-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1a2332] hover:bg-[#2c3e50] text-white font-semibold h-11 transition-all"
                data-testid="login-submit-button"
              >
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </div>
          <p className="text-center text-slate-400 text-xs mt-6">
            AppDroid AI Innovations Pvt Ltd — Employee Portal
          </p>
        </div>
      </div>
    </div>
  );
}
